import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getBusinessPlan, getHybrideBillingWindow, addBpRow, updateBpMonth, deleteBpRow, linkBpRowToClient, getClientOptions, CHANNELS, CHANNEL_LABELS, channelLabel, type BusinessPlan, type HybrideBilling, type Channel, getBpEcartContrats, type BpEcart } from '../../lib/refonteApi';
import { CONTRACT_LABELS, type ContractType } from '../../lib/appointmentLifecycle';
import StatTile from '../../components/refonte/StatTile';
import ContractBadge from '../../components/refonte/ContractBadge';
import { Skeleton } from '../../components/refonte/Skeleton';
import EmptyState from '../../components/refonte/EmptyState';

const MOIS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
const eur = (n: number) => Math.round(n).toLocaleString('fr-FR') + ' €';
const eurK = (n: number) => (n >= 1000 ? (n / 1000).toFixed(1).replace('.0', '') + 'k' : String(Math.round(n)));
const firstNonZero = (m: number[]) => m.findIndex(x => x > 0);

// Cellule mois éditable façon sheet. Affiche le total (base + variable) ; l'édition écrit la BASE (forecast).
// Non éditable si la ligne n'a pas de bpId (ligne agrégée multi-contrats → on édite les sous-lignes).
const BpCell: React.FC<{ display: number; base: number; bpId: string | null; mi: number; cur: boolean; small?: boolean; onSave: (bpId: string, mi: number, v: number) => Promise<void> }> = ({ display, base, bpId, mi, cur, small, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  const [saving, setSaving] = useState(false);
  const [ecart, setEcart] = useState<BpEcart | null>(null); // BP saisi vs contrats (mois courant)
  const cls = `py-2 px-1 text-right font-mono ${small ? 'text-[11px]' : ''} ${display === 0 && !editing ? 'text-faint' : cur ? 'text-hook-rose font-semibold' : ''}`;
  if (!bpId) return <td className={cls}>{display ? display.toLocaleString('fr-FR') : '·'}</td>;
  const commit = async () => {
    setEditing(false);
    const n = Number(val.replace(/\s/g, '').replace(',', '.'));
    if (!Number.isFinite(n) || n === base) return;
    setSaving(true); try { await onSave(bpId, mi, n); } finally { setSaving(false); }
  };
  return (
    <td className={cls}>
      {editing ? (
        <input autoFocus type="number" value={val} onChange={e => setVal(e.target.value)} onBlur={commit}
          onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setEditing(false); }}
          className="w-16 text-right rounded border border-hook-rose bg-panel px-1 py-0.5 text-[11px]" />
      ) : (
        <button onClick={() => { setVal(base ? String(base) : ''); setEditing(true); }} title="Cliquer pour éditer le forecast" className={`hover:underline decoration-dotted ${saving ? 'opacity-40' : ''}`}>
          {display ? display.toLocaleString('fr-FR') : '·'}
        </button>
      )}
    </td>
  );
};

const DirectionBpPage: React.FC = () => {
  const [bp, setBp] = useState<BusinessPlan | null>(null);
  const [hyb, setHyb] = useState<HybrideBilling | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<'all' | ContractType>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'actif' | 'inactif'>('all');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<'name' | 'formule' | 'status' | 'total'>('total');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ clientId: '', name: '', channel: 'cold_call' as Channel, contractType: 'fixed' as 'fixed' | 'fixed_plus_variable' | 'gtm', monthly: '', startMonth: String(new Date().getMonth() + 2 > 12 ? 12 : new Date().getMonth() + 2), endMonth: '12' });
  const [clientOpts, setClientOpts] = useState<{ id: string; name: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [ecart, setEcart] = useState<BpEcart | null>(null); // BP saisi vs contrats (mois courant)
  const curMonth = new Date().getMonth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Plus de catch muet : si le compteur hybride casse, la direction doit le VOIR (audit 08/10 — il
      // était silencieusement absent un mois sur deux à cause d'une date invalide).
      const [b, h] = await Promise.all([
        getBusinessPlan(),
        getHybrideBillingWindow().catch((e: any) => { setErr('Compteur hybride indisponible : ' + (e?.message ?? e)); return null; }),
      ]);
      setBp(b); setHyb(h);
      getBpEcartContrats().then(setEcart).catch(() => setEcart(null));
    }
    catch (e: any) { setErr(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { getClientOptions().then(setClientOpts).catch(() => {}); }, []);

  // Édition inline d'une cellule du BP → écrit la base bp_import puis recharge.
  const saveCell = useCallback(async (bpId: string, mi: number, v: number) => {
    try { await updateBpMonth(bpId, mi, v); await load(); } catch (e: any) { setErr(e.message); }
  }, [load]);
  const [linkEdit, setLinkEdit] = useState<{ bpId: string; clientId: string } | null>(null);
  const doDeleteBp = async (bpId: string) => { if (!confirm('Supprimer cette ligne du BP ?')) return; try { await deleteBpRow(bpId); await load(); } catch (e: any) { setErr(e.message); } };
  const doLinkBp = async () => { if (!linkEdit?.clientId) return; try { await linkBpRowToClient(linkEdit.bpId, linkEdit.clientId); setLinkEdit(null); await load(); } catch (e: any) { setErr(e.message); } };

  const submit = async () => {
    if (!form.name.trim() || !form.monthly) return;
    setSaving(true);
    try {
      await addBpRow({
        name: form.name.trim(), clientId: form.clientId || null, channel: form.channel, contractType: form.contractType,
        monthly: Number(form.monthly), startMonth: Number(form.startMonth), endMonth: Number(form.endMonth),
      });
      setShowForm(false);
      setForm({ ...form, clientId: '', name: '', monthly: '' });
      await load();
    } catch (e: any) { setErr(e.message); }
    setSaving(false);
  };

  if (loading) return (
    <div><h1 className="text-[21px] font-bold tracking-tight mb-5">Business plan 2026</h1>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-[92px] rounded-card" />)}</div>
      <Skeleton className="h-[320px] rounded-card" />
    </div>
  );
  // Fatal = pas de BP du tout. Une erreur NON-fatale (compteur hybride, échec d'une édition
  // inline) s'affiche dans le bandeau plus bas SANS vider la page (audit UX 09/10, finding #1 :
  // le setErr du compteur hybride effaçait tout le tableau).
  if (!bp) return <div><h1 className="text-[21px] font-bold mb-4">Business plan 2026</h1><EmptyState tone="error" title="Chargement impossible" hint={err || ''} /></div>;

  // « actif » = a du CA ce mois-ci ou plus tard (contrat en cours), déduit du prévisionnel
  // — PAS le flag CRM statut (souvent périmé).
  const activeNow = (m: number[]) => m.slice(curMonth).some(x => x > 0);

  // Les filtres (offre + statut + recherche) s'appliquent à TOUTE la page (KPIs + graphe + détail),
  // pour que le CA en haut réponde aux boutons (retour Kevin 25/09). Le « canal » est retiré (redondant
  // avec l'offre). Les ventilations plus bas restent calculées sur TOUTE la data.
  const allRows = bp.rows;
  const totalAll = allRows.reduce((s, r) => s + r.total, 0);
  const q = search.trim().toLowerCase();
  const rows = allRows.filter(r =>
    (filterType === 'all' || r.contractType === filterType) &&
    (filterStatus === 'all' || (filterStatus === 'actif' ? activeNow(r.months) : !activeNow(r.months))) &&
    (q === '' || r.name.toLowerCase().includes(q) || (r.lines ?? []).some(l => l.name.toLowerCase().includes(q))));
  const monthlyTotals = Array.from({ length: 12 }, (_, i) => rows.reduce((s, r) => s + r.months[i], 0));
  const annualTotal = monthlyTotals.reduce((s, x) => s + x, 0);
  const maxMonth = Math.max(...monthlyTotals, 1);
  const curTotal = monthlyTotals[curMonth];
  const activeThisMonth = rows.filter(r => r.months[curMonth] > 0).length;

  const types: ContractType[] = ['fixed', 'variable', 'gtm', 'days'];
  const mix = types.map(t => ({
    t, total: allRows.filter(r => r.contractType === t).reduce((s, r) => s + r.total, 0),
  })).filter(m => m.total > 0);
  const channelMix = CHANNELS.map(ch => ({
    ch, total: allRows.filter(r => r.channel === ch).reduce((s, r) => s + r.total, 0), count: allRows.filter(r => r.channel === ch).length,
  })).filter(m => m.count > 0);

  const monthsRemaining = (months: number[]) => months.slice(curMonth).filter(x => x > 0).length;
  const nextIdx = curMonth + 1;
  const startsNext = nextIdx <= 11 ? rows.filter(r => firstNonZero(r.months) === nextIdx) : [];
  const startsNextRevenue = startsNext.reduce((s, r) => s + r.months[nextIdx], 0);

  // TABLEAU DÉTAILLÉ = les mêmes lignes filtrées, triées.
  const toggleSort = (k: typeof sortKey) => { if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey(k); setSortDir(k === 'name' ? 'asc' : 'desc'); } };
  const detailRows = [...rows].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortKey === 'name') return a.name.localeCompare(b.name) * dir;
    if (sortKey === 'formule') return String(a.contractType).localeCompare(String(b.contractType)) * dir;
    if (sortKey === 'status') return (Number(activeNow(a.months)) - Number(activeNow(b.months))) * dir;
    return (a.total - b.total) * dir;
  });
  const detailMonthlyTotals = Array.from({ length: 12 }, (_, i) => detailRows.reduce((s, r) => s + r.months[i], 0));
  const detailAnnualTotal = detailMonthlyTotals.reduce((s, x) => s + x, 0);
  const sortArrow = (k: typeof sortKey) => sortKey === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  return (
    <div>
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
        <h1 className="text-[21px] font-bold tracking-tight">Business plan 2026</h1>
        <div className="flex items-center gap-3">
          <span className="font-mono text-[11px] text-faint">prévisionnel · {bp.rows.length} clients</span>
          <button onClick={() => setShowForm(v => !v)} className="text-[12px] font-semibold px-3 py-1.5 rounded-md bg-ink text-white hover:opacity-90">+ Ajouter au BP</button>
        </div>
      </div>
      <p className="text-dim text-[13px] mb-5">Revenu prévisionnel mensuel par client · source : BP importé</p>

      {/* Garde-fou « un seul CA » : BP saisi vs contrats actifs du mois (audit 08/10 — Kevin voyait
          trois CA différents selon l'écran ; l'écart est désormais AFFICHÉ au lieu d'être silencieux). */}
      {ecart && Math.abs(ecart.bpFixe - ecart.contratsFixe) > 1 && (
        <div className="mb-4 border border-st-warn/40 bg-st-warn/5 rounded-card px-4 py-3 text-[12.5px]">
          ⚠️ <b>Écart BP ↔ contrats ({ecart.ym})</b> : BP saisi <b>{ecart.bpFixe.toLocaleString('fr-FR')} €</b> ·
          contrats actifs <b>{ecart.contratsFixe.toLocaleString('fr-FR')} €</b> ·
          écart <b>{(ecart.bpFixe - ecart.contratsFixe).toLocaleString('fr-FR')} €</b>.
          Le contrat est la vérité : corrige le BP ou le contrat, pas les deux.
        </div>
      )}

      {err && <div className="mb-4 text-[12px] text-st-bad bg-st-bad/10 rounded px-3 py-2">{err}</div>}


      {/* Formulaire d'ajout */}
      {showForm && (
        <div className="bg-panel border border-line-2 rounded-card p-4 mb-4">
          <h2 className="font-mono text-[10px] uppercase tracking-widest text-dim mb-1">Ajouter un client au BP</h2>
          <p className="text-[11.5px] text-dim mb-3">Client existant → on le relie. <b>Nouveau nom</b> → on <b>crée le client</b> dans ta base (onboarding à compléter) et on lie la ligne dessus.</p>
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1"><span className="font-mono text-[9px] uppercase text-faint">Client (existant)</span>
              <select value={form.clientId} onChange={e => { const id = e.target.value; const c = clientOpts.find(o => o.id === id); setForm({ ...form, clientId: id, name: c ? c.name : form.name }); }} className="rounded-md border border-line-2 bg-panel px-2 py-1.5 text-[13px] w-48">
                <option value="">— nouveau client (créé) —</option>
                {clientOpts.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select></label>
            <label className="flex flex-col gap-1"><span className="font-mono text-[9px] uppercase text-faint">Nom (au BP)</span>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Nom" className="rounded-md border border-line-2 bg-panel px-2 py-1.5 text-[13px] w-40" /></label>
            <label className="flex flex-col gap-1"><span className="font-mono text-[9px] uppercase text-faint">Canal</span>
              <select value={form.channel} onChange={e => setForm({ ...form, channel: e.target.value as Channel })} className="rounded-md border border-line-2 bg-panel px-2 py-1.5 text-[13px]">
                {CHANNELS.map(c => <option key={c} value={c}>{CHANNEL_LABELS[c]}</option>)}
              </select></label>
            <label className="flex flex-col gap-1"><span className="font-mono text-[9px] uppercase text-faint">Formule</span>
              <select value={form.contractType} onChange={e => setForm({ ...form, contractType: e.target.value as any })} className="rounded-md border border-line-2 bg-panel px-2 py-1.5 text-[13px]">
                <option value="fixed">Forfait</option><option value="fixed_plus_variable">Hybride</option><option value="gtm">GTM</option>
              </select></label>
            <label className="flex flex-col gap-1"><span className="font-mono text-[9px] uppercase text-faint">Montant/mois €</span>
              <input type="number" value={form.monthly} onChange={e => setForm({ ...form, monthly: e.target.value })} placeholder="2600" className="rounded-md border border-line-2 bg-panel px-2 py-1.5 text-[13px] w-28" /></label>
            <label className="flex flex-col gap-1"><span className="font-mono text-[9px] uppercase text-faint">Début</span>
              <select value={form.startMonth} onChange={e => setForm({ ...form, startMonth: e.target.value })} className="rounded-md border border-line-2 bg-panel px-2 py-1.5 text-[13px]">
                {MOIS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}</select></label>
            <label className="flex flex-col gap-1"><span className="font-mono text-[9px] uppercase text-faint">Fin</span>
              <select value={form.endMonth} onChange={e => setForm({ ...form, endMonth: e.target.value })} className="rounded-md border border-line-2 bg-panel px-2 py-1.5 text-[13px]">
                {MOIS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}</select></label>
            <button disabled={saving || !form.name || !form.monthly} onClick={submit} className="text-[13px] font-semibold px-4 py-1.5 rounded-md bg-hook-rose text-white disabled:opacity-40">{saving ? 'Ajout…' : 'Ajouter'}</button>
          </div>
        </div>
      )}

      {/* Filtres : offre + statut */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un client…" className="rounded-md border border-line-2 bg-panel px-2.5 py-1 text-[12.5px] w-52" />
        {search && <button onClick={() => setSearch('')} className="text-[12px] text-dim hover:text-ink" title="Effacer">✕</button>}
        <span className="w-px h-5 bg-line-2 mx-1" />
        <span className="font-mono text-[9px] uppercase tracking-widest text-faint mr-1">Offre</span>
        {([['all', 'Toutes'], ['fixed', CONTRACT_LABELS.fixed], ['variable', CONTRACT_LABELS.variable], ['gtm', CONTRACT_LABELS.gtm]] as const).map(([v, lbl]) => (
          <button key={v} onClick={() => setFilterType(v as any)}
            className={`text-[11.5px] font-semibold px-2.5 py-1 rounded-md border ${filterType === v ? 'bg-ink text-white border-ink' : 'border-line-2 text-dim hover:border-ink'}`}>{lbl}</button>
        ))}
        <span className="w-px h-5 bg-line-2 mx-1" />
        <span className="font-mono text-[9px] uppercase tracking-widest text-faint mr-1">Statut</span>
        {([['all', 'Tous'], ['actif', 'Actifs (ce mois+)'], ['inactif', 'Terminés']] as const).map(([v, lbl]) => (
          <button key={v} onClick={() => setFilterStatus(v)}
            className={`text-[11.5px] font-semibold px-2.5 py-1 rounded-md border ${filterStatus === v ? 'bg-ink text-white border-ink' : 'border-line-2 text-dim hover:border-ink'}`}>{lbl}</button>
        ))}
        <span className="ml-auto font-mono text-[11px] text-faint">{rows.length} client(s)</span>
      </div>

      {/* KPIs (recalculés selon les filtres) */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <StatTile value={eur(annualTotal)} label="CA annuel prévu" />
        <StatTile value={eur(curTotal)} label={`CA ${MOIS[curMonth].toLowerCase()} (mois courant)`} />
        <StatTile value={eur(hyb?.grandTotal || 0)} label="Variable hybride (RDV × prix)" />
        <StatTile value={activeThisMonth} label="Clients actifs ce mois" />
        <StatTile value={startsNext.length} label={nextIdx <= 11 ? `Démarrent en ${MOIS[nextIdx].toLowerCase()}` : 'Démarrages'} hint={startsNext.length ? `+${eur(startsNextRevenue)}` : undefined} />
      </div>

      {/* Contrats qui démarrent le mois prochain */}
      {startsNext.length > 0 && (
        <div className="bg-panel border border-line rounded-card p-4 mb-4">
          <h2 className="font-mono text-[10.5px] uppercase tracking-widest text-dim mb-3">🆕 Démarrages en {MOIS[nextIdx].toLowerCase()} · {startsNext.length} contrat(s) · +{eur(startsNextRevenue)}/mois</h2>
          <div className="flex flex-wrap gap-2">
            {startsNext.map((r, i) => (
              <span key={i} className="inline-flex items-center gap-2 border border-line rounded-lg px-2.5 py-1 text-[12px]">
                <span className="font-semibold">{r.name}</span><ContractBadge contractType={r.contractType} />
                <span className="font-mono text-hook-rose">{eur(r.months[nextIdx])}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Courbe mensuelle */}
      <div className="bg-panel border border-line rounded-card p-5 mb-4">
        <h2 className="font-mono text-[10.5px] uppercase tracking-widest text-dim mb-4">Revenu par mois</h2>
        <div className="flex items-end gap-2 h-[180px]">
          {monthlyTotals.map((v, i) => (
            <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
              <span className="font-mono text-[9px] text-dim mb-1">{v > 0 ? eurK(v) : ''}</span>
              <div className={`w-full rounded-t ${i === curMonth ? 'bg-hook-rose' : 'bg-ink/70'}`}
                style={{ height: `${Math.max(2, (v / maxMonth) * 140)}px` }} title={eur(v)} />
              <span className={`font-mono text-[9px] mt-1 ${i === curMonth ? 'text-hook-rose font-bold' : 'text-faint'}`}>{MOIS[i]}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Ventilation par canal (où rentre l'oseille) */}
      <div className="bg-panel border border-line rounded-card p-4 mb-4">
        <h2 className="font-mono text-[10.5px] uppercase tracking-widest text-dim mb-3">Ventilation par canal · où rentre le CA</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {channelMix.map(m => (
            <div key={m.ch} className="border border-line rounded-card p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-[12.5px]">{channelLabel(m.ch)}</span>
                <span className="font-mono text-[10px] text-faint">{totalAll ? Math.round((m.total / totalAll) * 100) : 0}%</span>
              </div>
              <div className="font-mono text-[16px] font-bold">{eur(m.total)}</div>
              <div className="font-mono text-[10px] text-faint">{m.count} client(s)</div>
            </div>
          ))}
        </div>
      </div>

      {/* Mix par formule */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {mix.map(m => (
          <div key={m.t} className="bg-panel border border-line rounded-card p-3">
            <div className="flex items-center justify-between mb-1">
              <ContractBadge contractType={m.t} />
              <span className="font-mono text-[10px] text-faint">{annualTotal ? Math.round((m.total / annualTotal) * 100) : 0}%</span>
            </div>
            <div className="font-mono text-[16px] font-bold">{eur(m.total)}</div>
          </div>
        ))}
      </div>

      {/* Tableau client × mois */}
      <div className="bg-panel border border-line rounded-card p-5">
        <h2 className="font-mono text-[10.5px] uppercase tracking-widest text-dim mb-4">Détail par client · {CONTRACT_LABELS.fixed}/{CONTRACT_LABELS.variable}/{CONTRACT_LABELS.gtm}</h2>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px] text-[12px]">
            <thead>
              <tr className="border-b border-line-2">
                <th className="text-left font-mono text-[9px] uppercase tracking-widest text-faint pb-2 sticky left-0 bg-panel"><button onClick={() => toggleSort('name')} className="uppercase hover:text-ink">Client{sortArrow('name')}</button></th>
                <th className="text-left font-mono text-[9px] uppercase tracking-widest text-faint pb-2 px-2"><button onClick={() => toggleSort('formule')} className="uppercase hover:text-ink">Formule{sortArrow('formule')}</button> <button onClick={() => toggleSort('status')} className="uppercase hover:text-ink text-faint">· statut{sortArrow('status')}</button></th>
                {MOIS.map((m, i) => <th key={m} className={`text-right font-mono text-[9px] uppercase pb-2 px-1 ${i === curMonth ? 'text-hook-rose' : 'text-faint'}`}>{m}</th>)}
                <th className="text-right font-mono text-[9px] uppercase tracking-widest text-faint pb-2 pl-2">Reste</th>
                <th className="text-right font-mono text-[9px] uppercase tracking-widest text-ink pb-2 pl-3"><button onClick={() => toggleSort('total')} className="uppercase hover:text-hook-rose">Total{sortArrow('total')}</button></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {detailRows.map((r, idx) => (
                <React.Fragment key={idx}>
                  <tr className="hover:bg-panel-2">
                    <td className="py-2 font-semibold sticky left-0 bg-panel whitespace-nowrap">
                      {r.clientId
                        ? <Link to={`/app/clients/${r.clientId}`} className="hover:text-hook-rose hover:underline" title="Ouvrir la fiche client">{r.name}</Link>
                        : r.name}
                      {r.lines && <span className="ml-1.5 font-mono text-[9px] px-1.5 py-0.5 rounded bg-raise text-dim">{r.lines.length} contrats</span>}
                      {firstNonZero(r.months) === nextIdx && <span title={`Démarre en ${MOIS[nextIdx]}`} className="ml-1.5 font-mono text-[9px] text-hook-rose">🆕</span>}
                    </td>
                    <td className="py-2 px-2 whitespace-nowrap">
                      {!r.clientId ? (
                        linkEdit && r.bpId && linkEdit.bpId === r.bpId ? (
                          <span className="flex items-center gap-1">
                            <select value={linkEdit.clientId} onChange={e => setLinkEdit({ bpId: r.bpId!, clientId: e.target.value })} className="rounded border border-line-2 bg-panel px-1.5 py-0.5 text-[11px] max-w-[150px]">
                              <option value="">— choisir un client —</option>
                              {clientOpts.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                            </select>
                            <button onClick={doLinkBp} disabled={!linkEdit.clientId} className="font-mono text-[10px] text-hook-rose hover:underline disabled:opacity-40">lier</button>
                            <button onClick={() => setLinkEdit(null)} className="font-mono text-[10px] text-faint hover:text-ink">×</button>
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <span className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-st-warn/10 text-st-warn uppercase">⚠ sans compte</span>
                            {r.bpId && <button onClick={() => setLinkEdit({ bpId: r.bpId!, clientId: '' })} className="font-mono text-[10px] text-dim hover:text-ink hover:underline">rattacher</button>}
                            {r.bpId && <button onClick={() => doDeleteBp(r.bpId!)} className="font-mono text-[10px] text-st-bad hover:underline">supprimer</button>}
                          </span>
                        )
                      ) : (
                        <>
                          {!r.lines && <><span className="font-mono text-[9px] text-dim mr-1.5">{channelLabel(r.channel)}</span><ContractBadge contractType={r.contractType} /></>}
                          <span className={`ml-1.5 font-mono text-[9px] px-1.5 py-0.5 rounded ${activeNow(r.months) ? 'bg-st-ok/10 text-st-ok' : 'bg-raise text-faint'}`}>{activeNow(r.months) ? 'actif' : 'terminé'}</span>
                          {/* Badges contrat : uniquement pour les comptes actifs (on ne se soucie pas du contrat sur inactif/terminé). */}
                          {r.status === 'actif' && ((r.contractMonthly == null || r.contractMonthly === 0)
                            ? <span className="ml-1.5 font-mono text-[9px] px-1.5 py-0.5 rounded bg-st-warn/10 text-st-warn uppercase" title="Client actif sans contrat — pas lancé">sans contrat</span>
                            : <span className="ml-1.5 font-mono text-[9px] text-faint" title="Montant mensuel du contrat actif (référence)">contrat {eurK(r.contractMonthly)}</span>)}
                          {r.status === 'actif' && r.contractMonthly != null && r.contractMonthly !== 0 && activeNow(r.months) && r.baseMonths[curMonth] !== r.contractMonthly && (
                            <span className="ml-1 font-mono text-[9px] px-1.5 py-0.5 rounded bg-st-warn/10 text-st-warn" title={`Forecast ${MOIS[curMonth]} = ${eurK(r.baseMonths[curMonth])} ≠ contrat ${eurK(r.contractMonthly)}`}>≠ contrat</span>
                          )}
                        </>
                      )}
                    </td>
                    {r.months.map((v, i) => (
                      <BpCell key={i} display={v} base={r.baseMonths[i]} bpId={r.bpId} mi={i} cur={i === curMonth} onSave={saveCell} />
                    ))}
                    <td className="py-2 pl-2 text-right font-mono text-dim">{monthsRemaining(r.months)}m</td>
                    <td className="py-2 pl-3 text-right font-mono font-bold">{eur(r.total)}</td>
                  </tr>
                  {/* Sous-lignes : les contrats du client (ex. Avenria = GTM + Data) */}
                  {r.lines?.map((l, j) => (
                    <tr key={`${idx}-${j}`} className="hover:bg-panel-2 bg-panel-2/40">
                      <td className="py-1.5 sticky left-0 bg-panel whitespace-nowrap">
                        <span className="font-mono text-[10px] text-faint ml-3 mr-1.5">└</span>
                        <span className="text-[11.5px] text-dim">{l.name}</span>
                      </td>
                      <td className="py-1.5 px-2 whitespace-nowrap">
                        <span className="font-mono text-[9px] text-dim mr-1.5">{channelLabel(l.channel)}</span>
                        <ContractBadge contractType={l.contractType} />
                      </td>
                      {l.months.map((v, i) => (
                        <BpCell key={i} display={v} base={l.baseMonths[i]} bpId={l.bpId} mi={i} cur={i === curMonth} small onSave={saveCell} />
                      ))}
                      <td className="py-1.5 pl-2 text-right font-mono text-[11px] text-faint">{monthsRemaining(l.months)}m</td>
                      <td className="py-1.5 pl-3 text-right font-mono text-[11px] text-dim">{eur(l.total)}</td>
                    </tr>
                  ))}
                  {/* Détail hybride : le fixe (forfait) et la variable (RDV validés × prix). */}
                  {r.variableMonths.some(v => v > 0) && (
                    <>
                      <tr className="bg-panel-2/40">
                        <td className="py-1.5 sticky left-0 bg-panel whitespace-nowrap"><span className="font-mono text-[10px] text-faint ml-3 mr-1.5">└</span><span className="text-[11.5px] text-dim">Fixe (forfait)</span></td>
                        <td className="py-1.5 px-2"></td>
                        {r.baseMonths.map((v, i) => <td key={i} className={`py-1.5 px-1 text-right font-mono text-[11px] ${v === 0 ? 'text-faint' : 'text-dim'}`}>{v ? v.toLocaleString('fr-FR') : '·'}</td>)}
                        <td></td>
                        <td className="py-1.5 pl-3 text-right font-mono text-[11px] text-dim">{eur(r.baseMonths.reduce((s, x) => s + x, 0))}</td>
                      </tr>
                      <tr className="bg-panel-2/40">
                        <td className="py-1.5 sticky left-0 bg-panel whitespace-nowrap"><span className="font-mono text-[10px] text-faint ml-3 mr-1.5">└</span><span className="text-[11.5px] text-dim">Variable · {r.pricePerRdv ? `RDV validé × ${eur(r.pricePerRdv)}` : 'RDV validés'}</span></td>
                        <td className="py-1.5 px-2"></td>
                        {r.variableMonths.map((v, i) => {
                          const n = r.pricePerRdv ? Math.round(v / r.pricePerRdv) : 0;
                          return <td key={i} className={`py-1.5 px-1 text-right font-mono text-[11px] ${v === 0 ? 'text-faint' : 'text-hook-rose'}`} title={v ? eur(v) : ''}>{v ? (r.pricePerRdv ? `${n}×` : v.toLocaleString('fr-FR')) : '·'}</td>;
                        })}
                        <td></td>
                        <td className="py-1.5 pl-3 text-right font-mono text-[11px] text-hook-rose">{eur(r.variableMonths.reduce((s, x) => s + x, 0))}</td>
                      </tr>
                    </>
                  )}
                </React.Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-line-2 font-bold">
                <td className="pt-2 font-mono text-[10px] uppercase text-dim sticky left-0 bg-panel" colSpan={2}>Total {detailRows.length !== bp.rows.length ? `(${detailRows.length} filtré${detailRows.length > 1 ? 's' : ''})` : ''}</td>
                {detailMonthlyTotals.map((v, i) => <td key={i} className={`pt-2 px-1 text-right font-mono ${i === curMonth ? 'text-hook-rose' : ''}`}>{v ? v.toLocaleString('fr-FR') : '·'}</td>)}
                <td></td>
                <td className="pt-2 pl-3 text-right font-mono">{eur(detailAnnualTotal)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        <p className="font-mono text-[10px] text-faint mt-3">Montants exacts (€). « Reste » = mois restants avec CA prévu à partir du mois courant. · À venir : croisement avec la capacité SDR/GTM pour repérer la saturation.</p>
      </div>
    </div>
  );
};

export default DirectionBpPage;
